import { listAllMastraThreadMessages } from '@fruitful/llm-service-shared';
import { useQuery } from '@tanstack/react-query';

import { captureException } from '../../logger/Logger';
import { useMastraClient } from '../providers/MastraClient';

export interface RawThreadMessage {
    id: string;
    role: string;
    createdAt: Date;
    message: unknown;
}

const toDate = (value: unknown): Date => {
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return new Date(value);
    }
    return new Date();
};

export const useGetMastraThreadRawMessages = ({
    threadId,
    agentId,
    refetchInterval,
}: {
    threadId?: string;
    agentId?: string;
    refetchInterval?: number | false;
}) => {
    const mastraClient = useMastraClient();

    return useQuery({
        queryKey: ['mastraThreadRawMessages', threadId, agentId],
        refetchInterval,
        queryFn: async (): Promise<RawThreadMessage[]> => {
            if (threadId == null || agentId == null) {
                return [];
            }

            try {
                const thread = mastraClient.getMemoryThread({
                    threadId,
                    agentId,
                });
                const messages = await listAllMastraThreadMessages(
                    async (params) => await thread.listMessages(params),
                );
                return messages.map((message) => ({
                    id: message.id,
                    role: message.role,
                    createdAt: toDate(message.createdAt),
                    message,
                }));
            } catch (err) {
                captureException(err);
                throw err;
            }
        },
        enabled: threadId != null && agentId != null,
    });
};
